import type { editor } from "monaco-editor";

/**
 * Returns the editor's content if it was edited before @monaco-editor/react called onMount and no longer
 * matches `value`, and undefined otherwise. @monaco-editor/react subscribes to content changes only after
 * onMount, so such an edit (only possible programmatically) never reaches onChange.
 *
 * @param monacoEditor The editor passed to onMount
 * @param value The value the editor was rendered with
 * @returns The edited content to report through onChange, or undefined when there is nothing to report
 */
export const getEditMadeBeforeMount = (
  monacoEditor: Pick<editor.ICodeEditor, "getModel">,
  value: unknown
): string | undefined => {
  const model = monacoEditor.getModel();
  // A model starts at version 1 and every edit increments it, so an unedited model has nothing to report
  // even when Monaco has normalized line endings or dropped a byte order mark from the initial text
  if (!model || model.getVersionId() === 1) {
    return undefined;
  }

  const editorValue = model.getValue();
  // @monaco-editor/react also edits the model to apply a value that changed before mount; that content
  // matches `value` once its line endings are converted the way Monaco converts them
  const expectedValue = String(value ?? "")
    .replace(/^\uFEFF/, "")
    .replace(/\r\n|\r|\n/g, model.getEOL());
  return editorValue === expectedValue ? undefined : editorValue;
};
